/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useQueueForm.ts (Queues)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Queue form state, validation, and submission.
    Handles create, update, and duplicate operations.

────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createQueue, updateQueue } from '../api/queuesApi';
import { FormState, TierRow } from '../types';
import { getDefaultFormState } from '../utils/formDefaults';
import { validateForm } from '../utils/validators';
import { buildSettingsFromFormState } from '../utils/settingsHelpers';
import { normalizeTierRows } from '../utils/tierHelpers';
import { combineDestination } from '../utils/destinationHelpers';

/**
 * Hook for managing Queue form
 */
export function useQueueForm(
  id: string | undefined,
  duplicateId: string | null,
  initialMappedData: { formState: FormState; tiers: TierRow[]; extraSettings: Record<string, unknown> } | null
) {
  const location = useLocation();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();

  const isEditMode = Boolean(id && id !== 'new');
  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [originalFormState, setOriginalFormState] = useState<FormState>(defaultFormState);
  const [tierRows, setTierRows] = useState<TierRow[]>([]);
  const [originalTierRows, setOriginalTierRows] = useState<TierRow[]>([]);
  const [extraSettings, setExtraSettings] = useState<Record<string, unknown>>({});
  const [originalExtraSettings, setOriginalExtraSettings] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const initializedRef = useRef(false);
  const lastInitialDataRef = useRef<string | null>(null);
  const lastIdRef = useRef<string | undefined>(undefined);
  const lastLocationKeyRef = useRef<string>('');

  // Reset form when id changes
  useEffect(() => {
    if (lastIdRef.current !== id) {
      lastIdRef.current = id;
      initializedRef.current = false;
      lastInitialDataRef.current = null;
    }
  }, [id]);

  // Initialize form data when initialMappedData changes
  useEffect(() => {
    const locationKey = location.key || location.pathname + location.search;
    const locationChanged = lastLocationKeyRef.current !== locationKey;

    if (initialMappedData) {
      const dataStr = JSON.stringify(initialMappedData);
      const lastDataStr = lastInitialDataRef.current;

      // Always update if:
      // 1. Data changed (different JSON string)
      // 2. Not initialized yet
      // 3. Location changed (navigation occurred, e.g., "Save and New")
      if (dataStr !== lastDataStr || !initializedRef.current || locationChanged) {
        lastInitialDataRef.current = dataStr;
        lastLocationKeyRef.current = locationKey;
        setFormState({ ...initialMappedData.formState });
        setOriginalFormState(duplicateId ? getDefaultFormState() : { ...initialMappedData.formState });
        setTierRows([...initialMappedData.tiers]);
        setOriginalTierRows(duplicateId ? [] : initialMappedData.tiers.map(tier => ({ ...tier })));
        setExtraSettings({ ...initialMappedData.extraSettings });
        setOriginalExtraSettings(duplicateId ? {} : { ...initialMappedData.extraSettings });
        setErrors({});
        initializedRef.current = true;
      }
    } else if (!isEditMode && !duplicateId) {
      // Reset refs when navigating to new form to ensure clean state
      // If location changed, force reset even if already initialized
      if (locationChanged) {
        initializedRef.current = false;
        lastInitialDataRef.current = null;
      }

      // New form - initialize with defaults
      if (!initializedRef.current) {
        lastLocationKeyRef.current = locationKey;
        const defaultState = getDefaultFormState();
        setFormState({ ...defaultState });
        setOriginalFormState({ ...defaultState });
        setTierRows([]);
        setOriginalTierRows([]);
        setExtraSettings({});
        setOriginalExtraSettings({});
        setErrors({});
        initializedRef.current = true;
      }
    }
  }, [initialMappedData, isEditMode, duplicateId, location]);

  const handleFieldChange = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleTiersChange = useCallback((tiers: TierRow[]) => {
    setTierRows(tiers);
  }, []);

  const handleExtraSettingsChange = useCallback((settings: Record<string, unknown>) => {
    setExtraSettings(settings);
  }, []);

  const hasChanges = useMemo(() => {
    const formChanged = Object.keys(formState).some((key) => {
      const formKey = key as keyof FormState;
      return formState[formKey] !== originalFormState[formKey];
    });

    const tiersChanged = JSON.stringify(tierRows) !== JSON.stringify(originalTierRows);
    const settingsChanged = JSON.stringify(extraSettings) !== JSON.stringify(originalExtraSettings);

    return formChanged || tiersChanged || settingsChanged;
  }, [formState, tierRows, extraSettings, originalFormState, originalTierRows, originalExtraSettings]);

  // Handle save - Always-Active Save Standard: always attempt to save
  const handleSave = useCallback(async () => {
    const trimmedName = formState.queueName.trim();
    if (!trimmedName) {
      const error = new Error(t('queues.messages.validationError'));
      showError(error.message);
      throw error;
    }

    const validationErrors = validateForm(formState, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const errorMsgs = Object.values(validationErrors).filter(msg => typeof msg === 'string').join(' • ');
      const error = new Error(`${t('queues.messages.validationError')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      showError(error.message);
      throw error;
    }

    setIsSaving(true);
    try {
      const normalizedTiers = normalizeTierRows(tierRows);
      const settings = buildSettingsFromFormState(formState);
      const mergedSettings = { ...settings, ...extraSettings };

      // IVR pattern: name = display name, context = normalized slug (lowercase)
      const effectiveContext = (formState.queueSlug || formState.queueName)
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_\-]/g, '')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .trim() || 'default';

      const payload = {
        name: formState.queueName.trim() || effectiveContext,  // display name
        context: effectiveContext,                              // slug → FreeSWITCH identifier
        strategy: formState.strategy,
        musicOnHold: formState.musicOnHold.trim() || 'local_stream://default',
        maxWaitTime: Number(formState.maxWaitTime) || 0,
        recordCalls: formState.recordCalls,
        enabled: formState.enabled,
        settings: mergedSettings,
        tiers: normalizedTiers,
        queueProfileId: formState.queueProfileId && formState.queueProfileId.trim() !== '' ? Number(formState.queueProfileId) : null,
        callbackProfileId: formState.callbackProfileId && formState.callbackProfileId.trim() !== '' ? Number(formState.callbackProfileId) : null,
        vipListIds: formState.vipListIds || [],
        extension: formState.extension.trim() || null,
        greeting: formState.greeting.trim() || null,
        language: formState.language.trim() || null
      };

      const result = isEditMode
        ? await updateQueue(id!, payload)
        : await createQueue(payload);

      showSuccess(t('queues.messages.saveSuccess'));

      // Update original state
      setOriginalFormState({ ...formState });
      setOriginalTierRows(tierRows.map(tier => ({ ...tier })));
      setOriginalExtraSettings({ ...extraSettings });

      // Return saved data for navigation
      return {
        id: result?.id || id,
        uuid: result?.uuid || result?.id || id,
        ...result
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('queues.messages.saveError');
      showError(message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [formState, tierRows, extraSettings, hasChanges, id, isEditMode, showError, showSuccess, showInfo, t]);

  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormState({ ...originalFormState });
      setTierRows(originalTierRows.map(tier => ({ ...tier })));
      setExtraSettings({ ...originalExtraSettings });
      setErrors({});
      return;
    }

    const defaultState = getDefaultFormState();
    setFormState(defaultState);
    setOriginalFormState({ ...defaultState });
    setTierRows([]);
    setOriginalTierRows([]);
    setExtraSettings({});
    setOriginalExtraSettings({});
    setErrors({});
  }, [isEditMode, originalFormState, originalTierRows, originalExtraSettings]);

  return {
    formState,
    tierRows,
    extraSettings,
    errors,
    isSaving,
    hasChanges,
    handleFieldChange,
    handleTiersChange,
    handleExtraSettingsChange,
    handleSave,
    handleCancel
  };
}

